import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";

import { useDispatch } from "react-redux";
import { useTranslation, withTranslation } from "react-i18next";

import PaginationContainer from "@plannel/components/PaginationContainer";
import Dialog from "@plannel/components/Dialog";
import ExcelExportButton from "@plannel/components/ExcelExportButton";
import Snackbar from "@plannel/components/Snackbar";
import DataState from "@plannel/components/aggrid/DataState";
import DefaultGridSetting, { nullSafeComparator } from "@plannel/components/aggrid/DefaultGridSetting";
import AttributeConfigModal from "@plannel/components/modal/AttributeConfigModal";

import GridUtils from "@plannel/components/aggrid/GridUtils";
import { AddButton, RemoveButton, SaveButton, SettingsButton, FilterButton } from "@plannel/components/ActionIconButton";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import ColorUtil from "../utils/color-util";
import CustomerAutocomplete from "@plannel/components/filter/CustomerAutocomplete";
import ItemAutocomplete from "@plannel/components/filter/ItemAutocomplete";
import bfFeatureSalesService from "@plannel/services/data/bf-feature-sales-service";
import { isEmpty } from "lodash";
import customerService from "@plannel/services/data/customer-service";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import GridDatePicker from "@plannel/components/GridDatePicker";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";

import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
const CURRENT_DATE = new ZDate();
const START_MIN_DATE = new ZDate(CURRENT_DATE.getFullYear() - 10, 0, 1);
const START_DATE = new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth(), 1);
const END_DATE = new ZDate(CURRENT_DATE.getFullYear() + 1, CURRENT_DATE.getMonth(), 0);
const END_MAX_DATE = new ZDate(CURRENT_DATE.getFullYear() + 5, CURRENT_DATE.getMonth() + 1, 0);
const initPeriod = { start: START_DATE, end: END_DATE };

const PeriodFilter = ({ period, setPeriod }) => {
  const { t } = useTranslation();

  const onChange = (e, type) => {
    const dateCopy = new Date(e);

    if (type) {
      if (type === "start") {
        dateCopy.setDate(1);
        dateCopy.setHours(0, 0, 0, 0);
      } else if (type === "end") { 
        const year = dateCopy.getFullYear();
        const month = dateCopy.getMonth();
        const lastDay = new Date(year, month + 1, 0).getDate();

        dateCopy.setDate(lastDay);
        dateCopy.setHours(23, 59, 59, 999);
      }

      setPeriod({ ...period, [type]: dateCopy });
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DatePicker
        label={t("periodStart")}
        views={["year", "month"]}
        format={"yyyy-MM"}
        value={period.start}
        maxDate={period.end ? period.end : END_MAX_DATE}
        minDate={START_MIN_DATE}
        onChange={e => onChange(e, "start")}
        slotProps={{ textField: { InputProps: { color: "primary", size: "small", sx: { mr: 1, width: 150 }} } }}
      />
      <DatePicker
        label={t("periodEnd")}
        views={["year", "month"]}
        format={"yyyy-MM"}
        value={period.end}
        maxDate={END_MAX_DATE}
        minDate={period.start ? period.start : START_MIN_DATE}
        onChange={e => onChange(e, "end")}
        slotProps={{ textField: { InputProps: { color: "primary", size: "small", sx: { mr: 1, width: 150 }} } }}
      />
    </LocalizationProvider>
  );
};

const BfFeatureSales = ({ t, viewName, title }) => {
  const { snackBarMessageColor } = ColorUtil;
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const pageSizes = [100, 200, 500];
  const [resultTotalCount, setResultTotalCount] = useState(null);
  const [currentPage, setCurrentPage] = useState(reduxViewState?.currentPage ?? 1);
  const [tPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(reduxViewState?.pageSize ?? pageSizes[0]);
  const [sortParams, setSortParams] = useState({});
  const itemSearch = useRef(reduxViewState?.itemSearch ?? "");
  const extraItemCodesRef = useRef(reduxViewState?.extraItemCodes ?? []);
  const customerSearch = useRef(reduxViewState?.customerSearch ?? "");
  const extraCustomerCodesRef = useRef(reduxViewState?.extraCustomerCodes ?? []);
  const [advancedFilters, setAdvancedFilters] = useState(reduxViewState?.advancedFilters ? reduxViewState.advancedFilters : null);
  const [openFilter, setOpenFilter] = useState(false);
  const [advancedFilterColor, setAdvancedFilterColor] = useState(!isEmpty(reduxViewState?.advancedFilters) ? "info" : "default");

  const [period, setPeriod] = useState(reduxViewState?.start && reduxViewState?.end ? { start: reduxViewState.start, end: reduxViewState.end } : initPeriod);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  const [attrConfigModalOpen, setAttrConfigModalOpen] = useState(false);

  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);
  const [featureColDefs, setFeatureColDefs] = useState([]);
  const attrColsCollapsed = reduxViewState && Object.prototype.hasOwnProperty.call(reduxViewState, "attrColsCollapsed") ? reduxViewState.attrColsCollapsed : false;

  const [customers, setCustomers] = useState([]);
  const [bfDatas, setBfDatas] = useState([]);

  const featureField = () => {
    const fields = [];
    for (let idx = 0; idx < 20; idx++) {
      fields.push(`feature${String(idx + 1).padStart(2, "0")}`);
    }
    return fields;
  };

  const dropdownValueFormatter = data => {
    return data.value ? data.value.name : "";
  };

  const customerFormatter = params => {
    const filterVal = customers.filter(arr => arr.customerCd === params.value);
    return filterVal.length > 0 ? filterVal[0].name : null;
  };

  const getRequestParams = () => {
    const params = {};
    const searchFilters = {};

    if (period?.start && period?.end) {
      searchFilters.startDate = period.start;
      searchFilters.endDate = period.end;
    }

    if (extraCustomerCodesRef.current?.length > 0) {
      searchFilters.customers = [customerSearch.current, ...extraCustomerCodesRef.current].filter(Boolean);
    } else if (customerSearch.current) {
      searchFilters.customer = customerSearch.current;
    }

    if (extraItemCodesRef.current?.length > 0) {
      searchFilters.items = [itemSearch.current, ...extraItemCodesRef.current].filter(Boolean);
    } else if (itemSearch.current) {
      searchFilters.item = itemSearch.current;
    }

    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      params.advancedFilters = advancedFilters;
    }

    if (currentPage) {
      params.page = currentPage - 1;
    }

    if (pageSize) {
      params.pageSize = pageSize;
    }

    params.searchFilters = searchFilters;

    return { ...params, ...sortParams };
  };

  const retrieveBfFeatureSales = () => {
    const params = getRequestParams();
    bfFeatureSalesService
      .get(params)
      .then(response => {
        const { results, totalPages, totalItems } = response.data;
        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }
        setBfDatas(results);
        setTotalPages(totalPages);
        setResultTotalCount(totalItems)
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        const reduxState = reduxUtil.createObjectState(viewName, { itemSearch: itemSearch.current, extraItemCodes: extraItemCodesRef.current, customerSearch: customerSearch.current, extraCustomerCodes: extraCustomerCodesRef.current, currentPage, pageSize, start: period.start, end: period.end, advancedFilters });
        reduxDispatch(reduxUtil.updateViewState(reduxState));
        DataState.initialize(gridRef.current?.api);
      });
  };

  const retrieveCustomers = () => {
    customerService
      .getLookup()
      .then(response => {
        const { results } = response.data;
        setCustomers(results);
      })
      .catch(e => {
        debug(e);
      });
  };

  const refreshGrid = () => {
    retrieveBfFeatureSales();
  };

  const columnDefs = useMemo(() => {
    featureColDefs.forEach((colDef) => {
      if (!colDef.hide) {
        colDef.filterType = "number";
        colDef.filterName = colDef.field;
      }
    });

    return [
      {
        headerName: "customerCd",
        field: "customer.customerCd",
        colId: "customerCd",
        minWidth: 200,
        type: ["nonEditableColumn"],
        flex: 2,
        cellClass: "stringType", filterType: "string"
      },
      {
        headerName: "customerName",
        field: "customer",
        colId: "customerName",
        cellEditor: "agRichSelectCellEditor",
        cellEditorPopup: true,
        type: ["defaultValueParser"],
        valueFormatter: dropdownValueFormatter,
        cellEditorParams: {
          valueFormatter: dropdownValueFormatter,
          values: customers,
        },
        filter: "agSetColumnFilter",
        filterParams: {
          valueFormatter: customerFormatter,
        },
        comparator: (value1, value2, nodeA, nodeB, isDescending) => {
          const val1 = !value1 ? "" : value1.name;
          const val2 = !value2 ? "" : value2.name;
          
          return nullSafeComparator(val1, val2, isDescending);
        },
        minWidth: 150,
        flex: 2, filterType: "string"
      },
      { headerName: "itemCd", field: "itemCd", width: 180, cellClass: 'stringType', filterType: "string" },
      { headerName: "itemName", field: "itemNm", type: ["nonEditableColumn"], width: 250, filterType: "string" },
      { headerName: "date", field: "baseDate", width: 200, type: ["defaultValueParser"], cellEditor: GridDatePicker, cellEditorParams: { dateType: "day" }, filterType: "date", valueFormatter: (data) => dateUtils.formatDate(data.value, "yyyy-MM-dd")},
      ...featureColDefs,
      { headerName: "createdTs", field: "createdTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, filterType: 'timestamp' },
      { headerName: "createdBy", field: "createdUser", colId: "createdBy", type: "nonEditableColumn", width: 150, filterType: 'string' },
      { headerName: "updatedTs", field: "updatedTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, filterType: 'timestamp' },
      { headerName: "updatedBy", field: "updatedUser", colId: "updatedBy", type: "nonEditableColumn", width: 150, filterType: 'string' },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featureColDefs, customers]);

  const insertRow = () => {
    const newRow = {
      id: null,
      baseDate: null,
      itemCd: null,
      itemNm: null,
      customerCd: null,
      customerNm: null,
      feature01: null,
      feature02: null,
      feature03: null,
      feature04: null,
      feature05: null,
      feature06: null,
      feature07: null,
      feature08: null,
      feature09: null,
      feature10: null,
      feature11: null,
      feature12: null,
      feature13: null,
      feature14: null,
      feature15: null,
      feature16: null,
      feature17: null,
      feature18: null,
      feature19: null,
      feature20: null,
      verNum: null,
    };

    const addRow = gridRef.current.api.applyTransaction({ add: [newRow], addIndex: 0 }).add[0];
    DataState.setDataState(addRow);
  };

  const removeBfFeatureSales = () => {
    const bfFeatureSalesIds = [];

    gridRef.current.api.getSelectedRows().forEach(data => {
      bfFeatureSalesIds.push(data.id);
    });

    if (bfFeatureSalesIds.length > 0) {
      bfFeatureSalesService.remove(bfFeatureSalesIds)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
        })
        .catch(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") });
        })
        .then(() => {
          refreshGrid();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  };

  const saveBfFeatureSales = () => {
    gridRef.current.api.stopEditing();
    const allStateData = DataState.getAllStateData(gridRef.current.api);
    if (allStateData.length > 0) {
      bfFeatureSalesService.upsert(allStateData)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        })
        .then(() => {
          retrieveBfFeatureSales();
        })
        .catch(e => {
          const { message } = e.response?.data;
          if (message) {
            setSnackInfo({ open: true, severity: message.type, content: t(message.message) });
          } else {
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
          }
          debug(e);
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  };

  const handleDialog = action => {
    if (action === "SAVE") {
      saveBfFeatureSales();
    } else if (action === "DELETE") {
      removeBfFeatureSales();
    }
  };

  const initPageToRetrieve = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveBfFeatureSales();
    }
  };

  const onGridReady = params => {
    retrieveCustomers();
    
    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
  };

  const collapseFilter = _event => {
    setOpenFilter(!openFilter);
  };

  useEffect(() => {
    if (gridLoading) {
      DataState.initialize(gridRef.current?.api);
      retrieveBfFeatureSales();
    }

    return () => {
      setBfDatas([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, gridLoading, sortParams]);

  useEffect(() => {
    if (gridLoading) {
      initPageToRetrieve();
    }

    return () => {
      setBfDatas([]);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, advancedFilters]);

  useEffect(() => {
    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      setAdvancedFilterColor("info");
    } else {
      setAdvancedFilterColor("default");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters]);

  return (
    <>
      <FilterContainer>
        <PeriodFilter
          period={period}
          setPeriod={setPeriod}
        />
        <CustomerAutocomplete
          retrieve={initPageToRetrieve}
          searchValue={customerSearch}
          useAccessFilter={false}
          sx={{ mr: 1 }}
          extraCodesRef={extraCustomerCodesRef}
        />
        <ItemAutocomplete
          retrieve={initPageToRetrieve}
          searchValue={itemSearch}
          sx={{ mr: 1 }}
          extraCodesRef={extraItemCodesRef}
        />
        <Box sx={{ ml: "auto" }}>
          <ExcelExportButton
            title={title}
            service={bfFeatureSalesService}
            columnDefs={columnDefs}
            totalRows={resultTotalCount}
            params={getRequestParams()}
          />
          <AddButton onClick={insertRow} />
          <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: t("MSG_DeleteConfirm") })} />
          <SaveButton onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
          <SettingsButton
            label="settings"
            onClick={() => setAttrConfigModalOpen(true)}
          />
          <FilterButton label="advancedFilter" advancedFilterColor={advancedFilterColor} onClick={collapseFilter} />
        </Box>
      </FilterContainer>
      <Box className="ag-theme-balham grid-area" mb={1}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', height: '100%', mr: 1.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <AgGridReact
              ref={gridRef}
              rowData={bfDatas}
              {...DefaultGridSetting({ title, viewName })}
              rowSelection={{
                ...DefaultGridSetting({ title, viewName }).rowSelection,
                checkboxes: true,
                headerCheckbox: true,
              }}
              columnDefs={GridUtils.getColumnDefs(columnDefs)}
              onGridReady={onGridReady}
              onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
            />
          </Box>
          {openFilter && (
            <ResizableFilterPanel>
              <AdvancedFilter
                columnDefs={columnDefs}
                advancedFilters={advancedFilters}
                onApply={setAdvancedFilters}
                onClose={() => setOpenFilter(false)}
              />
            </ResizableFilterPanel>
          )}
        </Box>
      </Box>
      <PaginationContainer
        currentPage={currentPage}
        totalPages={tPages}
        pageSize={pageSize}
        pageSizes={pageSizes}
        setCurrentPage={setCurrentPage}
        setPageSize={setPageSize}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
        disableButton={dialogInfo.disableButton}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <AttributeConfigModal
        viewName={viewName}
        open={attrConfigModalOpen}
        setOpen={setAttrConfigModalOpen}
        target={"sales"}
        attrFields={featureField()}
        setAttrColDefs={setFeatureColDefs}
        attrColumnOptions={{ valueParser: params => String(params.newValue) }}
        attrColsCollapsed={attrColsCollapsed}
        attrColumnGroupOptions={{ columnGroupShow: "open", type: ["rightAligned"] }}
      />
    </>
  );
};

export default withTranslation()(BfFeatureSales);
